import React from 'react';
import { Mail, ShieldOff, Ticket } from 'lucide-react';
import { STATUS_OPTIONS, statusMeta } from './leadStatus';
import { formatManilaTime } from '../../../utils/formatManilaTime';
import { colorForLabel } from '../../../utils/colorHash';

const formatTime = (iso) => formatManilaTime(iso);

const LeadList = ({
  leads, selectedLeadId, onSelect, filter, setFilter, loading,
  regions, regionFilter, setRegionFilter, statusFilter, setStatusFilter,
}) => {
  const regionName = (code) => regions.find((r) => r.code === code)?.name || code || 'Default';
  return (
    <div className="w-full md:w-80 flex-shrink-0 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
      <div className="flex border-b border-slate-200" data-testid="leads-filter-tabs">
        {['all', 'unread'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`leads-filter-${f}`}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              filter === f ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="p-2 border-b border-slate-200 bg-slate-50 flex gap-2">
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          data-testid="leads-region-filter"
          className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
        >
          <option value="all">All regions</option>
          {regions.map((r) => (
            <option key={r.code} value={r.code}>{r.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="leads-status-filter"
          className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
      <div className="overflow-y-auto max-h-[65vh]" data-testid="leads-list">
        {loading && (
          <p className="text-sm text-slate-400 italic p-4">Loading leads…</p>
        )}
        {!loading && leads.length === 0 && (
          <p className="text-sm text-slate-400 italic p-4" data-testid="leads-list-empty">
            {filter === 'unread' ? 'No unread conversations.' : 'No leads yet.'}
          </p>
        )}
        {leads.map((lead) => {
          const meta = statusMeta(lead.status);
          return (
            <button
              key={lead.lead_id}
              onClick={() => onSelect(lead.lead_id)}
              data-testid={`lead-row-${lead.lead_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors hover:bg-slate-50 ${
                selectedLeadId === lead.lead_id ? 'bg-slate-100' : ''
              } ${lead.unread ? 'bg-amber-50/60' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm truncate ${lead.unread ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                  {lead.name || 'Unknown'}
                </span>
                <span className="text-xs text-slate-400 whitespace-nowrap">{formatTime(lead.last_message_at)}</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                {lead.unread && <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" data-testid={`lead-unread-dot-${lead.lead_id}`} />}
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colorForLabel(lead.region || 'default') }}
                  data-testid={`lead-region-badge-${lead.lead_id}`}
                  title={regionName(lead.region)}
                />
                <span className="text-xs text-slate-400 truncate">{regionName(lead.region)}</span>
                <span className="flex items-center gap-1 flex-shrink-0" data-testid={`lead-health-flags-${lead.lead_id}`}>
                  {lead.awaiting_reply && (
                    <Mail className="w-3 h-3 text-blue-500" data-testid={`lead-flag-awaiting-reply-${lead.lead_id}`} title="Awaiting your reply" />
                  )}
                  {lead.has_suppressed && (
                    <ShieldOff className="w-3 h-3 text-purple-500" data-testid={`lead-flag-suppressed-${lead.lead_id}`} title="Had a Safe Test Mode suppressed send" />
                  )}
                  {lead.assigned_via_voucher && (
                    <span
                      className="flex items-center gap-0.5 text-emerald-600"
                      data-testid={`lead-flag-voucher-${lead.lead_id}`}
                      title="Auto-assigned via voucher code"
                    >
                      <Ticket className="w-3 h-3" />
                      {lead.voucher_code && (
                        <span
                          className="text-[10px] font-semibold bg-emerald-50 border border-emerald-200 rounded px-1 leading-tight"
                          data-testid={`lead-voucher-code-${lead.lead_id}`}
                        >
                          {lead.voucher_code}
                        </span>
                      )}
                    </span>
                  )}
                </span>
                <span
                  data-testid={`lead-status-badge-${lead.lead_id}`}
                  className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${meta.badge}`}
                >
                  {meta.label}
                </span>
              </div>
              <p className={`text-xs mt-1 truncate ${lead.unread ? 'text-slate-700' : 'text-slate-400'}`}>
                {lead.last_message_preview || 'No messages yet'}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LeadList;
